import * as React from 'react'
import { ReactElement, useMemo } from 'react'
import { useFormikContext } from 'formik'
import { DateTime, Interval } from 'luxon'
import { Toggle } from '@fluentui/react'
import LegacyUserDropdown from '../../../Dropdown/LegacyUserDropdown'
import { getDropdownProps } from '../../../../helpers/formHelper'
import FileDropdown from '../../../Dropdown/File/FileDropdown'
import ReportForm from './ReportForm'
import DateRange from '../../../../components/DateRange/LegacyDateRange'
import { InvoiceTemplateDto, PrefillDeterminer, PrefillPeriod } from '../../../../config/finance/invoiceConfig'
import useEntity from '../../../../hooks/useEntity'
import Loading from '../../../../components/Loading'
import { DocumentGenerationType } from '../../../../hooks/document/useDocumentGenerationHandler'
import { getFilterInterval } from '../Invoice/InvoiceFile'

export type TimeReportDto = {
    from: string
    to: string
    files: string[]
    itemUsers: string[]
    fileOwners: string[]
    salesRate: boolean
}

function ReportBody(): ReactElement {
    const { errors, values, setFieldValue } = useFormikContext<TimeReportDto>()

    return (
        <>
            <DateRange
                startValue={values.from}
                endValue={values.to}
                changeStartDate={date => setFieldValue('from', date)}
                changeEndDate={date => setFieldValue('to', date)}
                showLabel={true}
            />
            <FileDropdown
                {...getDropdownProps(values.files, errors?.files, value => setFieldValue('files', value), true)}
                required={false}
            />
            <LegacyUserDropdown
                label="Dossierverantwoordelijke"
                {...getDropdownProps(
                    values.fileOwners,
                    errors?.fileOwners,
                    value => setFieldValue('fileOwners', value),
                    true,
                )}
                required={false}
            />
            <LegacyUserDropdown
                label="Uitvoerder"
                {...getDropdownProps(
                    values.itemUsers,
                    errors?.itemUsers,
                    value => setFieldValue('itemUsers', value),
                    true,
                )}
                required={false}
            />
            <Toggle
                label="Verkooptarieven"
                defaultChecked={values.salesRate}
                onText="Vertonen"
                offText="Verbergen"
                onChange={(event: React.MouseEvent<HTMLElement>, checked?: boolean) => {
                    setFieldValue('salesRate', checked)
                }}
            />
        </>
    )
}

export default function TimeReportForm(): ReactElement {
    const [templates] = useEntity<InvoiceTemplateDto[]>(() => ({ url: '/finance/invoice/template' }), false)
    const interval = useMemo(() => {
        return (
            templates?.data
                ?.filter(template => template.isDefault)
                .flatMap(template => template.lineItemGroups)
                .filter(group => group.prefillCutoff)
                .map(group =>
                    Interval.fromDateTimes(
                        getFilterInterval(PrefillPeriod.Year, PrefillDeterminer.Previous, group.prefillCutoff).start,
                        getFilterInterval(PrefillPeriod.Month, PrefillDeterminer.Previous, group.prefillCutoff).end,
                    ),
                )
                .find(interval => interval.isValid) ||
            Interval.fromDateTimes(
                DateTime.local().minus({ months: 12 }).startOf('month'),
                DateTime.local().minus({ months: 1 }).endOf('month'),
            )
        )
    }, [templates])

    if (templates.loading) {
        return <Loading label="Bezig met laden declaratietypes..." />
    }

    return (
        <ReportForm<TimeReportDto>
            title="Declaratieadvies"
            generationType={DocumentGenerationType.OutstandingCharges}
            initialValues={{
                from: interval.start.toISODate(),
                to: interval.end.toISODate(),
                files: [],
                fileOwners: [],
                itemUsers: [],
                salesRate: true,
            }}
        >
            <ReportBody />
        </ReportForm>
    )
}
